import type { LegacyRDMDOpts, RMDXishOpts, RMDXOpts, Variables } from '@readme/backend/iso/types/rdmd';

import { createCustomBlocksHash, mapCustomBlocksType } from '@readme/backend/iso';
import { useContext, useMemo } from 'react';

import { ProjectContext, VariablesContext, type ProjectContextValue } from '@core/context';
import useMediaQuery from '@core/hooks/useMediaQuery';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { ThemeContext } from '@Hub/Theme';

/**
 * Constructs the options object used by the RDMD renderer.
 * When a project is mdx/superhub enabled, the options are kept in sync with
 * the current custom blocks and glossary terms in state.
 *
 * @example
 * const rdmdOpts = useRdmdOpts();
 * <RDMD opts={rdmdOpts} />
 */
export default function useRdmdOpts({
  copyButtons = true,
  ...overrides
}: { isMdx?: boolean; isMdxish?: boolean; copyButtons?: boolean } = {}) {
  const variables = useContext(VariablesContext) as Variables;
  const { project } = useContext(ProjectContext) as ProjectContextValue;

  const [_isMdx, terms] = useProjectStore(s => [s.data.features.mdx === 'enabled', s.data.glossary]);
  const [customBlocks, isSuperHub] = useSuperHubStore(s => [s.document.customBlocks, s.isSuperHub]);
  const isMdx = overrides.isMdx ?? _isMdx;
  // For now, mdxish is possibly enabled only if we are in superhub
  const isMdxishEnabled = overrides.isMdxish ?? (project?.flags?.mdxish && isSuperHub) ?? false;
  const migratedFromMdx =
    project?.mdxishMigrationStatus?.migratedFrom === 'mdx' && !project?.mdxishMigrationStatus?.revertedAt;

  // Derive project theme to pass to the RDMD contexts
  const { colorMode } = useContext(ThemeContext); // initialized to project theme but tracks overrides via light/dark mode toggle
  const systemTheme = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';
  const theme = colorMode === 'system' ? systemTheme : colorMode;

  // Use when rendering MDX content
  const mdxOpts: RMDXOpts = useMemo(
    () => ({
      components: createCustomBlocksHash(customBlocks),
      copyButtons,
      terms,
      theme,
      variables,
    }),
    [customBlocks, copyButtons, terms, theme, variables],
  );

  // Use when rendering MDXish content
  const mdxishOpts: RMDXishOpts = useMemo(
    () => ({
      components: createCustomBlocksHash(customBlocks),
      componentTypes: mapCustomBlocksType(customBlocks),
      copyButtons,
      mdxish: true,
      ...(migratedFromMdx && { fallbackToMdx: true }),
      terms,
      theme,
      variables,
    }),
    [customBlocks, copyButtons, migratedFromMdx, terms, theme, variables],
  );

  // Use when rendering legacy Markdown content
  const legacyOpts: LegacyRDMDOpts = useMemo(
    () => ({
      compatibilityMode: !!project?.flags?.rdmdCompatibilityMode,
      copyButtons,
      correctnewlines: !!project?.flags?.correctnewlines,
      normalize: true,
      terms,
      theme,
      variables,
      reusableContent: { tags: createCustomBlocksHash(customBlocks), wrap: false },
    }),
    [
      project?.flags?.rdmdCompatibilityMode,
      project?.flags?.correctnewlines,
      copyButtons,
      terms,
      theme,
      variables,
      customBlocks,
    ],
  );

  if (isMdxishEnabled) return mdxishOpts;
  if (isMdx) return mdxOpts;
  return legacyOpts;
}
